import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from 'src/config/firebase';
import { CheckinItem, enrichClientCheckin } from 'src/api/checkins';

export interface CheckinSlotRaw {
  time: string;
  isAppoint?: boolean;
  appointUId?: string;
  clientName?: string;
  client_name?: string;
  checkinType?: string;
  status?: string;
  zoomMeetingUrl?: string;
  zoomMeetingId?: string;
  zoomMeetingPwd?: string;
  message_id?: string;
  checkinProgress?: string;
  [key: string]: any;
}

export interface CheckinDayDoc {
  date: string;
  morningList: CheckinSlotRaw[];
  daytimeList: CheckinSlotRaw[];
  eveningList: CheckinSlotRaw[];
}

export const useCheckinsRealtime = (trainerUid: string, bookedOnly: boolean = true) => {
  const [checkins, setCheckins] = useState<CheckinItem[]>([]);
  const [checkinDaysMap, setCheckinDaysMap] = useState<Record<string, CheckinDayDoc>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [activeUid, setActiveUid] = useState(
    () => auth.currentUser?.uid || (trainerUid && !trainerUid.includes('@') ? trainerUid : ''),
  );

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid) {
        setActiveUid(user.uid);
      } else {
        setActiveUid(trainerUid && !trainerUid.includes('@') ? trainerUid : '');
      }
    });
    return () => unsubscribeAuth();
  }, [trainerUid]);

  useEffect(() => {
    const uidToUse =
      activeUid ||
      auth.currentUser?.uid ||
      (trainerUid && !trainerUid.includes('@') ? trainerUid : '');

    if (!uidToUse) {
      return;
    }

    setIsLoading(true);

    // Query checkinList subcollection under trainer_app_users/{uidToUse}
    const checkinListCol = collection(db, 'trainer_app_users', uidToUse, 'checkinList');

    const unsubscribe = onSnapshot(
      checkinListCol,
      (snapshot) => {
        const items: CheckinItem[] = [];
        const daysMap: Record<string, CheckinDayDoc> = {};

        snapshot.docs.forEach((doc) => {
          const docData = doc.data() as Record<string, any>;
          const dateStr = docData.date || doc.id;

          const morning = (docData.morningList || []) as CheckinSlotRaw[];
          const daytime = (docData.daytimeList || []) as CheckinSlotRaw[];
          const evening = (docData.eveningList || []) as CheckinSlotRaw[];

          daysMap[dateStr] = {
            date: dateStr,
            morningList: morning,
            daytimeList: daytime,
            eveningList: evening,
          };

          ['morningList', 'daytimeList', 'eveningList'].forEach((listKey) => {
            const slotList = (docData[listKey] || []) as Array<Record<string, any>>;
            slotList.forEach((slot) => {
              if (!slot || typeof slot !== 'object') return;

              const isAppointed = Boolean(slot.isAppoint || slot.appointUId);
              // Option A: Only include booked student appointments, ignore empty availability slots
              if (bookedOnly && !isAppointed) return;

              items.push(enrichClientCheckin(slot, dateStr, uidToUse));
            });
          });
        });

        // Sort in memory by date + time descending
        items.sort((a, b) => {
          const dateA = `${a.date || ''}T${a.time || '00:00'}`;
          const dateB = `${b.date || ''}T${b.time || '00:00'}`;
          return dateB.localeCompare(dateA);
        });

        setCheckins(items);
        setCheckinDaysMap(daysMap);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching check-ins realtime from checkinList:', err);
        setError(err);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [trainerUid, activeUid, bookedOnly]);

  return { checkins, checkinDaysMap, isLoading, error };
};
